import React, { useEffect, useState } from 'react';
import { fetchEventDetail, bookRide, formatTime, RideEventDetail, RideEventSlot, RideProgress } from '../../services/rideService';
import PickupLocationPicker, { PickupLocation } from './PickupLocationPicker';

interface EventDetailSectionProps {
  eventId: string;
  onBack: () => void;
  onBooked: (state: BookedState) => void;
}

export interface BookedState {
  rideRequestId: string;
  accessToken: string;
  slot: { date: string; startTime: string; endTime: string };
  eventName: string;
  pickupAddress: string;
}

const EventDetailSection: React.FC<EventDetailSectionProps> = ({ eventId, onBack, onBooked }) => {
  const [data, setData] = useState<RideEventDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedSlot, setSelectedSlot] = useState<RideEventSlot | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    partySize: '1',
  });
  const [userPickup, setUserPickup] = useState<PickupLocation | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    fetchEventDetail(eventId)
      .then(setData)
      .catch(() => setError('Unable to load event details.'))
      .finally(() => setLoading(false));
  }, [eventId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const eventHasPickup = !!(data?.rideEvent?.pickup?.address && data?.rideEvent?.pickup?.coordinates?.lat);
  const needsUserPickup = !eventHasPickup;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSlot) return;

    if (needsUserPickup && !userPickup) {
      setSubmitError('Please select a pickup location on the map.');
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    try {
      const result = await bookRide({
        rideEventId: eventId,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        partySize: parseInt(formData.partySize),
        slotStartTime: selectedSlot.startTime,
        slotEndTime: selectedSlot.endTime,
        ...(needsUserPickup && userPickup ? {
          pickupAddress: userPickup.address,
          pickupLat: userPickup.lat,
          pickupLng: userPickup.lng,
        } : {}),
      });

      const pickupAddr = needsUserPickup
        ? (userPickup?.address || '')
        : (data?.rideEvent?.pickup?.address || '');

      onBooked({
        rideRequestId: result._id,
        accessToken: result.accessToken,
        slot: result.slot,
        eventName: data?.event?.name || data?.rideEvent?.title || '',
        pickupAddress: pickupAddr,
      });
    } catch (err: any) {
      setSubmitError(err.message || 'Booking failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-WHITE border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-16">
        <p className="text-red-300 mb-4">{error || 'Event not found'}</p>
        <button onClick={onBack} className="text-WHITE underline">Back to events</button>
      </div>
    );
  }

  const { rideEvent, event: popin, slots, totalRidesRemaining, rideProgress } = data;
  const heroImage = rideEvent.display?.heroImage || popin?.bannerImage || popin?.bannerImages?.medium;
  const maxParty = rideEvent.capacity.maxPartySize;

  return (
    <div className="py-2">
      {/* Back button */}
      <button onClick={onBack} className="text-WHITE/60 hover:text-WHITE text-sm mb-4 inline-block">
        ← All Events
      </button>

      {/* Hero */}
      <div className="relative h-48 md:h-56 rounded-xl overflow-hidden mb-6">
        {heroImage ? (
          <img src={heroImage} alt={popin?.name || rideEvent.title} className="w-full h-full object-cover opacity-80" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-7xl bg-BLACK/30 rounded-xl">
            {popin?.emoji || '🎉'}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-BLACK/80 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <h1 className="text-2xl md:text-3xl font-bold text-WHITE" style={{ fontFamily: '"Anton", sans-serif' }}>
            {popin?.name || rideEvent.title}
          </h1>
          <p className="text-WHITE/70 text-sm mt-1">
            {formatDate(rideEvent.schedule.date)} · {formatTime(rideEvent.schedule.startTime)} - {formatTime(rideEvent.schedule.endTime)}
          </p>
        </div>
      </div>

      {/* Ride Progress Banner */}
      {rideProgress && rideProgress.phase === 'in_progress' && (
        <div className="bg-GREEN/20 border border-GREEN/40 rounded-xl p-4 mb-6 flex items-center gap-3">
          <span className="w-3 h-3 bg-GREEN rounded-full animate-pulse flex-shrink-0" />
          <div>
            <p className="text-GREEN font-bold text-sm">Rides In Progress</p>
            <p className="text-WHITE/60 text-xs">Rides are underway.</p>
          </div>
        </div>
      )}
      {rideProgress && rideProgress.phase === 'completed' && (
        <div className="bg-BLUE_AF/30 border border-BLUE_AF/40 rounded-xl p-4 mb-6 flex items-center gap-3">
          <svg className="w-5 h-5 text-GREEN flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-WHITE font-bold text-sm">Event Complete</p>
            <p className="text-WHITE/60 text-xs">All riders have been dropped off.</p>
          </div>
        </div>
      )}

      {/* Event Info */}
      <div className="space-y-4 mb-8">
        {rideEvent.description && (
          <p className="text-WHITE/80 leading-relaxed">{rideEvent.description}</p>
        )}
        {rideEvent.display?.promoText && (
          <div className="bg-ORANGE/20 border border-ORANGE/40 rounded-lg p-4">
            <p className="text-ORANGE font-bold text-sm">{rideEvent.display.promoText}</p>
          </div>
        )}

        {eventHasPickup ? (
          <div className="bg-BLACK/30 rounded-lg p-4 border border-WHITE/10">
            <h3 className="text-xs text-WHITE/50 uppercase tracking-wide mb-2">Pickup Location</h3>
            <p className="font-bold text-WHITE">{rideEvent.pickup!.address}</p>
            {rideEvent.pickup!.instructions && (
              <p className="text-WHITE/50 text-sm mt-1">{rideEvent.pickup!.instructions}</p>
            )}
            {rideEvent.pickup!.mapLink && (
              <a href={rideEvent.pickup!.mapLink} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-ORANGE text-sm font-bold hover:underline">
                Open in Maps →
              </a>
            )}
          </div>
        ) : (
          <PickupLocationPicker value={userPickup} onChange={setUserPickup} />
        )}

        {totalRidesRemaining > 0 && (
          <div className="bg-BLACK/30 rounded-lg p-4 border border-WHITE/10 text-center">
            <p className="text-WHITE font-bold">Free rides available</p>
            <p className="text-WHITE/50 text-xs">Book your slot below</p>
          </div>
        )}
      </div>

      {/* Slot Picker + Booking */}
      <div className="border-t border-WHITE/10 pt-6">
        <h2 className="text-xl font-bold text-WHITE mb-4" style={{ fontFamily: '"Anton", sans-serif' }}>
          {totalRidesRemaining > 0 ? 'Pick Your Ride Slot' : 'All Rides Booked'}
        </h2>

        {totalRidesRemaining > 0 && (
          <>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 mb-6">
              {slots.map((slot) => {
                const isFull = slot.remaining <= 0;
                const isSelected = selectedSlot?.startTime === slot.startTime;

                return (
                  <button
                    key={slot.startTime}
                    onClick={() => !isFull && setSelectedSlot(slot)}
                    disabled={isFull}
                    className={`
                      py-2.5 px-2 rounded-lg text-center transition-all text-sm font-bold
                      ${isFull
                        ? 'bg-WHITE/5 text-WHITE/20 cursor-not-allowed'
                        : isSelected
                          ? 'bg-ORANGE text-WHITE ring-2 ring-ORANGE ring-offset-2 ring-offset-transparent'
                          : 'bg-BLACK/30 text-WHITE border border-WHITE/20 hover:border-ORANGE cursor-pointer'
                      }
                    `}
                  >
                    <div>{formatTime(slot.startTime)}</div>
                    {rideEvent.display?.showRemainingSlots !== false && !isFull && (
                      <div className="text-[10px] mt-0.5 opacity-60">{slot.remaining} left</div>
                    )}
                    {isFull && <div className="text-[10px] mt-0.5">Full</div>}
                  </button>
                );
              })}
            </div>

            {selectedSlot && (
              <form onSubmit={handleSubmit} className="bg-BLACK/40 backdrop-blur-sm rounded-xl p-5 border border-WHITE/10">
                <h3 className="text-WHITE font-bold mb-1">
                  Book your ride at {formatTime(selectedSlot.startTime)} - {formatTime(selectedSlot.endTime)}
                </h3>
                <p className="text-WHITE/50 text-sm mb-4">Fill in your details and we'll confirm your free ride.</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs text-WHITE/50 mb-1">First Name *</label>
                    <input
                      type="text" name="firstName" required value={formData.firstName} onChange={handleChange}
                      className="w-full bg-BLACK/40 border border-WHITE/20 rounded-lg px-3 py-2.5 text-WHITE text-sm focus:border-ORANGE focus:outline-none placeholder-WHITE/30"
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-WHITE/50 mb-1">Last Name *</label>
                    <input
                      type="text" name="lastName" required value={formData.lastName} onChange={handleChange}
                      className="w-full bg-BLACK/40 border border-WHITE/20 rounded-lg px-3 py-2.5 text-WHITE text-sm focus:border-ORANGE focus:outline-none placeholder-WHITE/30"
                      placeholder="Last name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs text-WHITE/50 mb-1">Email *</label>
                    <input
                      type="email" name="email" required value={formData.email} onChange={handleChange}
                      className="w-full bg-BLACK/40 border border-WHITE/20 rounded-lg px-3 py-2.5 text-WHITE text-sm focus:border-ORANGE focus:outline-none placeholder-WHITE/30"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-WHITE/50 mb-1">Phone</label>
                    <input
                      type="tel" name="phone" value={formData.phone} onChange={handleChange}
                      className="w-full bg-BLACK/40 border border-WHITE/20 rounded-lg px-3 py-2.5 text-WHITE text-sm focus:border-ORANGE focus:outline-none placeholder-WHITE/30"
                      placeholder="(optional)"
                    />
                  </div>
                </div>

                <div className="mb-5">
                  <label className="block text-xs text-WHITE/50 mb-1">Party Size *</label>
                  <select
                    name="partySize" required value={formData.partySize} onChange={handleChange}
                    className="w-full md:w-40 bg-BLACK/40 border border-WHITE/20 rounded-lg px-3 py-2.5 text-WHITE text-sm focus:border-ORANGE focus:outline-none"
                  >
                    {Array.from({ length: maxParty }, (_, i) => i + 1).map(n => (
                      <option key={n} value={n}>{n} {n === 1 ? 'person' : 'people'}</option>
                    ))}
                  </select>
                </div>

                {submitError && (
                  <div className="bg-red-500/20 border border-red-400/40 text-red-300 rounded-lg p-3 mb-4 text-sm">
                    {submitError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-ORANGE text-WHITE font-bold py-3 rounded-lg text-base hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {submitting ? 'Booking...' : 'Book My Free Ride'}
                </button>
              </form>
            )}
          </>
        )}

        {totalRidesRemaining === 0 && (
          <div className="text-center py-10 bg-BLACK/30 rounded-xl border border-WHITE/10">
            <p className="text-WHITE/50 text-lg mb-2">All rides for this event have been claimed.</p>
            <button onClick={onBack} className="text-ORANGE font-bold hover:underline">Check other events</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDetailSection;
